import { useCallback, useReducer, useRef } from "react";
import { DeepPartial } from "react-hook-form";
import i18n from "i18next";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { AavoWizard } from "src/components/common/wizard/AavoWizard.tsx";
import { AavoWizardStep } from "src/components/common/wizard/AavoWizardStep.tsx";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import {
	SalesLeadToOfferApi,
	SalesLeadToOfferApi_InitData,
} from "src/api/generated/erp/sales/crm/salesLeads/api/salesLeadToOfferApi.ts";
import { SalesLeadToOfferService_Result } from "src/api/generated/erp/sales/crm/salesLeads/service/salesLeadToOfferService.ts";
import { CustomerFormValues } from "src/components/views/erp/sales/customer/CustomerFormContent.tsx";
import { CustomerOfferFormValues } from "src/components/views/erp/sales/customerOrderOffer/CustomerOfferFormContent.tsx";
import {
	CustomerWizardStep,
	getDefaultCustomerValues,
} from "src/components/views/erp/sales/crm/salesLeadToOffer/CustomerWizardStep.tsx";
import {
	CustomerOfferWizardStep,
	getDefaultOfferValues,
} from "src/components/views/erp/sales/crm/salesLeadToOffer/CustomerOfferWizardStep.tsx";
import { CustomerOrderOffer } from "src/api/generated/erp/db/types/tables/customerOrderOffer.ts";
import {
	getCustomerContactsForSubmit,
	getCustomerInvoiceAddressForSubmit,
	getOfferAddressValuesFromCustomerAddress,
} from "src/components/views/erp/sales/crm/salesLeadToOffer/SalesLeadToOfferWizard.utils.ts";

interface SalesLeadToOfferWizardProps {
	salesLeadId: number;
	onCompleted: (result: SalesLeadToOfferService_Result) => void;
	onClose: () => void;
	onFormEdited?: () => void;
}

export const SalesLeadToOfferWizard = ({
	salesLeadId,
	onCompleted,
	onClose,
	onFormEdited,
}: SalesLeadToOfferWizardProps) => {
	return (
		<AsyncFetchRender
			fetch={() => SalesLeadToOfferApi.getInitData({ salesLeadId })}
			content={(initData) => (
				<SalesLeadToOfferWizardContent
					initData={initData}
					onCompleted={onCompleted}
					onClose={onClose}
					onFormEdited={onFormEdited}
				/>
			)}
		/>
	);
};

interface WizardContentProps {
	initData: SalesLeadToOfferApi_InitData;
	onCompleted: (result: SalesLeadToOfferService_Result) => void;
	onClose: () => void;
	onFormEdited?: () => void;
}

const SalesLeadToOfferWizardContent = ({ initData, onCompleted, onClose, onFormEdited }: WizardContentProps) => {
	const { defaultSiteId, appUserId } = useGlobalInitData();
	const { tenantConfig } = useTenantCustomizations();

	const needsCustomerCreation = initData.salesLead.customerId == null;

	const customerValuesRef = useRef<DeepPartial<CustomerFormValues>>(getDefaultCustomerValues(initData));

	const offerValuesRef = useRef<DeepPartial<CustomerOfferFormValues>>(
		getDefaultOfferValues({
			initData: initData,
			defaultSiteId: defaultSiteId,
			appUserId: appUserId,
			inputPricesWithVatByDefault: tenantConfig.erp.inputPricesWithVatByDefault ?? false,
			billingPlanEnabledByDefault: tenantConfig.erp.billingPlanEnabledByDefault ?? false,
		}),
	);
	const [, refreshDefaultValues] = useReducer((value: number) => value + 1, 0);

	const handleCustomerValuesUpdate = useCallback((values: DeepPartial<CustomerFormValues>) => {
		customerValuesRef.current = values;
	}, []);

	const handleOfferValuesUpdate = useCallback((values: DeepPartial<CustomerOfferFormValues>) => {
		offerValuesRef.current = values;
	}, []);

	const handleBeforeNext = useCallback(
		(currentStepIndex: number) => {
			if (!needsCustomerCreation || currentStepIndex !== 0 || customerValuesRef.current.deliveryAddress == null)
				return;

			const deliveryAddress = customerValuesRef.current.deliveryAddress;
			offerValuesRef.current = {
				...offerValuesRef.current,
				...getOfferAddressValuesFromCustomerAddress(deliveryAddress),
			};
			refreshDefaultValues();
		},
		[needsCustomerCreation],
	);

	return (
		<AavoWizard
			onSubmit={handleSubmit}
			onCancel={onClose}
			onBeforeNext={handleBeforeNext}
			onFormEdited={onFormEdited}
		>
			{needsCustomerCreation && (
				<AavoWizardStep label={i18n.t("customer")}>
					<CustomerWizardStep
						initData={initData}
						defaultValues={customerValuesRef.current}
						onUpdate={handleCustomerValuesUpdate}
					/>
				</AavoWizardStep>
			)}
			<AavoWizardStep label={i18n.t("offer")}>
				<CustomerOfferWizardStep
					initData={initData}
					defaultValues={offerValuesRef.current}
					onUpdate={handleOfferValuesUpdate}
				/>
			</AavoWizardStep>
		</AavoWizard>
	);

	async function handleSubmit() {
		const customer = customerValuesRef.current as CustomerFormValues;
		const result = await SalesLeadToOfferApi.submit({
			salesLeadId: initData.salesLead.salesLeadId,
			newCustomerData:
				needsCustomerCreation ?
					{
						customer: customer,
						deliveryAddress: customer.deliveryAddress,
						invoiceAddress: getCustomerInvoiceAddressForSubmit(customer),
						contacts: getCustomerContactsForSubmit(customer),
					}
				:	null,
			customerOffer: offerValuesRef.current as CustomerOrderOffer,
		});
		onCompleted(result);
	}
};
